import { supabase } from './supabaseClient';

const uploadHistoryService = {
  /**
   * Save an upload record to Supabase
   */
  async saveUpload(uploadData) {
    try {
      const session = supabase.getSession();
      const user = supabase.getUser();
      
      console.log('Saving upload - Full Session:', JSON.stringify(session, null, 2));
      console.log('Saving upload - User:', user);
      
      // Try to get user ID from different possible locations
      const userId = session?.user?.id || session?.user?.sub || user?.id || user?.sub;
      
      console.log('Resolved userId:', userId);
      
      if (!userId) {
        console.error('User not authenticated - cannot save upload. Session structure:', session);
        throw new Error('User not authenticated');
      }

      console.log('Attempting to insert upload for user:', userId);

      // Insert into uploads table
      const { data: upload, error: uploadError } = await supabase
        .from('uploads')
        .insert({
          user_id: userId,
          title: uploadData.title,
          description: uploadData.description || null,
          filename: uploadData.filename,
          file_size: uploadData.size,
          video_url: uploadData.videoUrl
        })
        .select()
        .single();

      console.log('Insert result - data:', upload, 'error:', uploadError);

      console.log('Upload saved:', upload);

      // Insert platform records
      const platformRecords = uploadData.platforms.map(platform => ({
        upload_id: upload.id,
        platform: platform,
        status: uploadData.results[platform] || 'uploading',
        platform_post_id: uploadData.platformPostIds?.[platform] || null,
        error_message: uploadData.errors?.[platform] || null
      }));

      console.log('Saving platform records:', platformRecords);

      const { error: platformError } = await supabase
        .from('upload_platforms')
        .insert(platformRecords);

      if (platformError) {
        console.error('Platform insert error:', platformError);
        throw platformError;
      }

      console.log('Upload history saved successfully');
      return { success: true, uploadId: upload.id };
    } catch (error) {
      console.error('Error saving upload to Supabase:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Load upload history from Supabase
   */
  async loadHistory() {
    try {
      const session = supabase.getSession();
      
      console.log('Loading history - Session:', session);
      
      if (!session || !session.user || !session.user.id) {
        console.log('No authenticated user - returning empty history');
        return [];
      }

      const userId = session.user.id;

      // Load uploads for current user
      const { data: uploads, error: uploadsError } = await supabase
        .from('uploads')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (uploadsError) {
        console.error('Error loading uploads:', uploadsError);
        throw uploadsError;
      }

      console.log('Loaded uploads from Supabase:', uploads);

      // Load all platform records
      const { data: platforms, error: platformsError } = await supabase
        .from('upload_platforms')
        .select('*');

      if (platformsError) {
        console.error('Error loading platforms:', platformsError);
        throw platformsError;
      }

      console.log('Loaded platforms from Supabase:', platforms);

      // Combine the data
      return uploads.map(upload => {
        const uploadPlatforms = platforms.filter(p => p.upload_id === upload.id);
        
        return {
          id: upload.id,
          title: upload.title,
          description: upload.description,
          filename: upload.filename,
          size: upload.file_size,
          videoUrl: upload.video_url,
          timestamp: upload.created_at,
          platforms: uploadPlatforms.map(p => p.platform),
          results: uploadPlatforms.reduce((acc, p) => {
            acc[p.platform] = p.status;
            return acc;
          }, {}),
          platformPostIds: uploadPlatforms.reduce((acc, p) => {
            if (p.platform_post_id) acc[p.platform] = p.platform_post_id;
            return acc;
          }, {}),
          errors: uploadPlatforms.reduce((acc, p) => {
            if (p.error_message) acc[p.platform] = p.error_message;
            return acc;
          }, {})
        };
      });
    } catch (error) {
      console.error('Error loading upload history from Supabase:', error);
      return [];
    }
  },

  /**
   * Delete an upload record
   */
  async deleteUpload(uploadId) {
    try {
      const { error } = await supabase
        .from('uploads')
        .delete()
        .eq('id', uploadId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting upload:', error);
      return { success: false, error: error.message };
    }
  }
};

export default uploadHistoryService;
